import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Plus, Save, X, RefreshCw, Search } from 'lucide-react';
import { PageContainer, PageHeader } from '../components/layout/Page';

interface Category {
  id: string;
  name: string;
  description: string | null;
  parent_category_id: string | null;
  organization_id: string | null;
  created_at: string;
  updated_at: string;
  subcategories?: Category[];
}

interface Organization {
  id: string;
  name: string;
}

export default function Categories() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_category_id: '',
    organization_id: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      fetchCategories();
      fetchOrganizations();
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      
      let query = supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoadError('No se pudo cargar el catálogo. Intenta actualizar de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) throw error;
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCategories();
    setRefreshing(false);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        parent_category_id: formData.parent_category_id || null,
        organization_id: formData.organization_id || null
      };

      if (editingCategory) {
        // Update existing category
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', editingCategory.id);

        if (error) throw error;
        alert('Categoría actualizada exitosamente');
      } else {
        // Create new category
        const { error } = await supabase
          .from('categories')
          .insert([categoryData]);

        if (error) throw error;
        alert('Categoría creada exitosamente');
      }

      // Reset form and refresh
      setShowAddForm(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', parent_category_id: '', organization_id: '' });
      await fetchCategories();
    } catch (error: any) {
      console.error('Error saving category:', error);
      alert(error.message || 'Error al guardar la categoría');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      parent_category_id: category.parent_category_id || '',
      organization_id: category.organization_id || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (categoryId: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta categoría? Esto también eliminará todas las subcategorías.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      alert('Categoría eliminada exitosamente');
      await fetchCategories();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      alert(error.message || 'Error al eliminar la categoría');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', parent_category_id: '', organization_id: '' });
    setFormErrors({});
  };

  const getAllCategories = (cats: Category[]): Category[] => {
    const result: Category[] = [];
    const addCategory = (cat: Category) => {
      result.push(cat);
      if (cat.subcategories) {
        cat.subcategories.forEach(addCategory);
      }
    };
    cats.forEach(addCategory);
    return result;
  };

  const filteredCategories = categories.filter(category =>
    (selectedOrg === 'all' || (selectedOrg === 'null' ? !category.organization_id : category.organization_id === selectedOrg)) &&
    `${category.name} ${category.description || ''}`.toLocaleLowerCase().includes(searchTerm.trim().toLocaleLowerCase())
  );

  return (
    <PageContainer>
        <PageHeader
          title="Categorías"
          description="Organiza los documentos por categoría y organización."
          actions={<>
            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="flex items-center gap-2 px-3 py-2 border border-slate-300 bg-white text-sm rounded-lg hover:bg-slate-100 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button
              onClick={() => { handleCancel(); setShowAddForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-sm font-medium rounded-lg hover:bg-blue-800"
            >
              <Plus className="w-4 h-4" />
              Nueva Categoría
            </button>
          </>}
        />

        {loadError && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</p>}
        {/* Filters */}
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="search"
                aria-label="Buscar en el catálogo"
                placeholder="Buscar categorías..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <select
                aria-label="Filtrar por organización"
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas las organizaciones</option>
                <option value="null">Sin organización</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            <div className="text-right text-sm text-gray-600 flex items-center justify-end">
              {filteredCategories.length} categoría{filteredCategories.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <h2 className="text-xl font-semibold mb-4">
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    aria-label="Nombre" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organización
                  </label>
                  <select
                    aria-label="Organización" value={formData.organization_id}
                    onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sin organización</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría Padre
                  </label>
                  <select
                    aria-label="Categoría padre" value={formData.parent_category_id}
                    onChange={(e) => setFormData({ ...formData, parent_category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sin categoría padre</option>
                    {getAllCategories(categories)
                      .filter(cat => cat.id !== editingCategory?.id)
                      .map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <input
                    type="text"
                    aria-label="Descripción" value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-sm font-medium rounded-lg hover:bg-blue-800"
                >
                  <Save className="w-4 h-4" />
                  {editingCategory ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-left text-sm" aria-label="Categorías">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium text-slate-500"><tr>
                <th scope="col" className="px-5 py-3">Categoría</th><th scope="col" className="px-5 py-3">Organización</th><th scope="col" className="px-5 py-3">Categoría padre</th><th scope="col" className="px-5 py-3 text-right">Acciones</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? <tr><td colSpan={4} className="p-8 text-center text-slate-500" role="status">Cargando categorías…</td></tr> : filteredCategories.map(category => <tr key={category.id} className="hover:bg-slate-50/70">
                  <td className="max-w-md px-5 py-4"><div className="font-medium text-slate-900">{category.name}</div>{category.description && <p className="mt-1 text-xs text-slate-500">{category.description}</p>}</td>
                  <td className="px-5 py-4 text-slate-600">{organizations.find(org => org.id === category.organization_id)?.name || (category.organization_id ? 'Organización no disponible' : 'Sin organización')}</td>
                  <td className="px-5 py-4 text-slate-600">{categories.find(parent => parent.id === category.parent_category_id)?.name || (category.parent_category_id ? 'Categoría no disponible' : '—')}</td>
                  <td className="px-5 py-4 text-right"><div className="flex items-center justify-end gap-3">
                    <button onClick={() => { handleEdit(category); window.scrollTo({top: 0, behavior: 'smooth'}); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100" aria-label={`Editar ${category.name}`}>Editar</button>
                    <details className="relative"><summary aria-label={`Más opciones para ${category.name}`} className="cursor-pointer rounded px-2 py-2 text-slate-500">Más</summary><button onClick={() => handleDelete(category.id)} className="mt-2 block rounded-lg border border-red-200 bg-white px-4 py-2 text-red-700">Eliminar</button></details>
                  </div></td>
                </tr>)}
                {!loading && !loadError && !filteredCategories.length && <tr><td colSpan={4} className="p-10 text-center text-slate-500">No hay categorías que coincidan con los filtros.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
    </PageContainer>
  );
}
